import { DateTime } from "luxon";

/**
 * Converts a Date object or string to UTC ISO format for backend storage
 */
export const toUTCForBackend = (date: Date | string | null): string | null => {
  if (!date) return null;

  return DateTime.fromJSDate(date instanceof Date ? date : new Date(date))
    .toUTC()
    .toISO();
};

/**
 * Formats a date from backend for display
 */
export const formatDateForDisplay = (
  date: Date | string | null,
  format = "dd-MM-yyyy",
  timezone?: string
): string => {
  if (!date) return "";

  const zone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  return DateTime.fromISO(
    date instanceof Date ? date.toISOString() : date.toString()
  )
    .setZone(zone)
    .toFormat(format);
};

/**
 * Converts a UTC date from backend to a local Date object for form inputs
 */
export const toLocalDate = (utcDate: string | Date | null): Date | null => {
  if (!utcDate) return null;

  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dateString = utcDate instanceof Date ? utcDate.toISOString() : utcDate;

  return DateTime.fromISO(dateString).setZone(zone).toJSDate();
};

/**
 * Adds days to a date, handling DST transitions properly
 */
export const addDays = (date: Date | string, days: number): Date => {
  const dateTime =
    date instanceof Date ? DateTime.fromJSDate(date) : DateTime.fromISO(date);

  return dateTime.plus({ days }).toJSDate();
};

/**
 * Checks if a date is in the past
 */
export const isInPast = (date: Date | string): boolean => {
  const dateTime =
    date instanceof Date ? DateTime.fromJSDate(date) : DateTime.fromISO(date);

  return dateTime < DateTime.now().startOf("day");
};

/**
 * Gets today's date at the start of the day
 */
export const getTodayStart = (): Date => {
  return DateTime.now().startOf("day").toJSDate();
};

/**
 * Converts a dayjs object to a Date object
 */
export const fromDayjsToDate = (dayjsDate: any): Date => {
  if (!dayjsDate) return new Date();
  return dayjsDate.toDate();
};

/**
 * Calculates end date based on start date, pace, and program length
 */
export const calculateEndDate = (
  startDate: Date | string | null,
  pace: number,
  programLength: number
): Date | null => {
  if (!startDate || !pace) return null;

  const dateTime =
    startDate instanceof Date
      ? DateTime.fromJSDate(startDate)
      : DateTime.fromISO(startDate.toString());

  const totalDays = pace * programLength;
  return dateTime.plus({ days: totalDays }).toJSDate();
};

export const formatISODate = (value: string) => {
  if (!value) {
    return { datePart: "", timePart: null };
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return { datePart: "", timePart: null };
  }

  const datePart = new Intl.DateTimeFormat("en-AU").format(date);

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  };

  const timePart = value.includes("T")
    ? new Intl.DateTimeFormat("en-AU", timeOptions).format(date)
    : null;

  return { datePart, timePart };
};
